/**
 * ReleaseItemCard
 *
 * One item of a release section on the dashboard: thumbnail, caption first line, version chip,
 * needed-by chip, "new" dot, a placement chip for released items and, per section, who sent it
 * or what the reviewer asked for. Click opens the global Release drawer in place (no navigation).
 * Desktop hover shows the shared dashboard post popover; tap opens the drawer directly.
 */

import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { VideoPlayBadge } from '@/components/ui/VideoPlayBadge';
import { RetryImage } from '@/components/ui/RetryImage';
import { TextPostTile } from '@/components/ui/TextPostTile';
import { ICONS, type IconName } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { HoverCard } from '@/components/ui/HoverCard';
import { DashboardPostPopover } from '@/components/dashboard/DashboardPostPopover';
import { NotificationBrandChip } from '@/components/notifications/NotificationBrandChip';
import {
  NEEDED_BY_CHIP_CLASS,
  NEEDED_BY_TEXT_CLASS,
  getNeededByInfo,
} from '@/components/release/neededBy';
import type { PostMedia } from '@/services/posts';
import { openReleaseDrawer } from '@/stores/releaseDrawer.store';
import { cn } from '@/utils/cn';
import { languageBadgeCode } from '@/utils/languages';
import type { ReleaseSectionKey, SectionItem } from '@/types/releaseSections';

const VIDEO_EXTENSION = /\.(mp4|mov|webm|avi|mkv|m4v)$/;

/** Video detection by explicit media type, else by URL extension. */
function firstMediaIsVideo(item: SectionItem, mediaUrls: string[]): boolean {
  if (item.post.mediaTypes?.[0] === 'VIDEO') return true;
  const first = mediaUrls[0];
  return first !== undefined && VIDEO_EXTENSION.test(first.toLowerCase().split('?')[0] ?? '');
}

const PLACEMENT_CHIP_CLASS: Record<'released' | 'scheduled' | 'live', string> = {
  released: 'bg-primary-50 text-primary-700',
  scheduled: 'bg-secondary-50 text-gray-800',
  live: 'bg-green-50 text-green-700',
};

export interface ReleaseItemCardProps {
  item: SectionItem;
  section: ReleaseSectionKey;
  isNew: boolean;
  /** Awaiting section: the requested changes were made (pinned first, marked) */
  pinned?: boolean;
  /** Portfolio scope: show which brand the item belongs to (a brand dashboard never does) */
  showBrand?: boolean;
}

/** Popover header gradient per status family (brand palette tokens). */
const POPOVER_GRADIENT = {
  review: 'from-primary-500 to-primary-600',
  changes: 'from-coral-500 to-coral-600',
  released: 'from-emerald-500 to-emerald-600',
} as const;

export const ReleaseItemCard = ({ item, section, isNew, pinned = false, showBrand = false }: ReleaseItemCardProps) => {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const { timezone, formatDateTime, formatRelativeDate } = useTimezone();

  const { post, variation } = item;
  const mediaUrls = variation?.mediaUrls?.length ? variation.mediaUrls : post.mediaUrls;
  const totalMedia = post.mediaCount ?? mediaUrls.length;
  const extraCount = totalMedia > 1 ? totalMedia - 1 : 0;
  const label = item.label.trim() || tc('untitled_post');

  // "Needed by" only matters while the item is still being worked on.
  const showNeededBy = section === 'awaiting' || section === 'changes_requested';
  const needed = showNeededBy ? getNeededByInfo(item.neededBy, timezone, tc) : null;

  const versionName = item.version.isOriginal
    ? null
    : item.version.label?.trim() || languageBadgeCode(item.version.language);

  // Placement chip (released items)
  let placement: { text: string; className: string } | null = null;
  if (item.placement === 'scheduled') {
    placement = {
      text: item.nextOccurrenceAt
        ? t('release_placement_scheduled', { when: formatDateTime(item.nextOccurrenceAt) })
        : t('release_placement_scheduled_plain'),
      className: PLACEMENT_CHIP_CLASS.scheduled,
    };
  } else if (item.placement === 'live') {
    placement = { text: t('release_placement_live'), className: PLACEMENT_CHIP_CLASS.live };
  } else if (item.placement === 'released' || section === 'ready_to_schedule') {
    placement = { text: t('release_placement_ready'), className: PLACEMENT_CHIP_CLASS.released };
  }

  // One meta line per section
  let meta: string | null = null;
  if (section === 'awaiting') {
    meta = pinned
      ? t('release_changes_by', { name: item.sentBy.name })
      : item.sentByMe
        ? t('release_sent_by_you')
        : t('release_sent_by', { name: item.sentBy.name });
  } else if (section === 'released_by_me' || section === 'ready_to_schedule') {
    meta = item.releasedAt
      ? tc('release_card_released_when', { when: formatRelativeDate(item.releasedAt) })
      : null;
  } else if (item.decision) {
    meta = t('release_decision_by', { name: item.decision.byName });
  }

  const openItem = (): void => {
    openReleaseDrawer({ postId: item.postId, variationId: item.variationId, source: 'dashboard' });
  };

  // ---- Popover (shared DashboardPostPopover) ----
  const isVideoItem = firstMediaIsVideo(item, mediaUrls);
  const popoverMedia: PostMedia[] = mediaUrls.map((url, index) => ({
    id: index,
    type: index === 0 && isVideoItem ? 'VIDEO' : 'IMAGE',
    originalUrl: url,
    thumbnailUrl: url,
    order: index,
  }));

  let statusLabel = tc('release_status_in_review');
  let gradient: string = POPOVER_GRADIENT.review;
  let statusIcon: IconName = ICONS.REVIEW;
  if (section === 'changes_requested') {
    statusLabel = tc('release_status_changes_requested');
    gradient = POPOVER_GRADIENT.changes;
    statusIcon = ICONS.CAUTION;
  } else if (item.placement === 'scheduled') {
    statusLabel = tc('release_status_scheduled');
    gradient = POPOVER_GRADIENT.released;
    statusIcon = ICONS.CALENDAR;
  } else if (item.placement === 'live') {
    statusLabel = tc('release_status_published');
    gradient = POPOVER_GRADIENT.released;
    statusIcon = ICONS.CHECK_CIRCLE;
  } else if (section === 'released_by_me' || section === 'ready_to_schedule') {
    statusLabel = tc('release_status_released');
    gradient = POPOVER_GRADIENT.released;
    statusIcon = ICONS.RELEASE_STAMP;
  }

  const senderText = item.sentByMe
    ? t('release_sent_by_you')
    : t('release_sent_by', { name: item.sentBy.name });
  const headerLines = [
    { icon: ICONS.USER, text: senderText },
    ...(item.approval.requestedAt
      ? [{ icon: ICONS.CLOCK, text: t('release_popover_sent_when', { when: formatDateTime(item.approval.requestedAt) }) }]
      : []),
  ];

  const detailRows: Array<{ key: string; label: string; value: string; extra?: string; extraClass?: string }> = [];
  const neededInfo = getNeededByInfo(item.neededBy, timezone, tc);
  if (item.neededBy && neededInfo && showNeededBy) {
    detailRows.push({
      key: 'needed',
      label: tc('release_filters_needed_by'),
      value: formatDateTime(item.neededBy),
      extra: neededInfo.relative,
      extraClass: NEEDED_BY_TEXT_CLASS[neededInfo.tone],
    });
  }
  if (item.placement === 'scheduled' && item.nextOccurrenceAt) {
    detailRows.push({ key: 'next', label: tc('release_status_scheduled'), value: formatDateTime(item.nextOccurrenceAt) });
  }
  if (item.placement === 'live' && item.liveAt) {
    detailRows.push({ key: 'live', label: tc('release_status_published'), value: formatDateTime(item.liveAt) });
  }
  if ((section === 'released_by_me' || section === 'ready_to_schedule') && item.releasedAt) {
    detailRows.push({ key: 'released', label: tc('release_status_released'), value: formatDateTime(item.releasedAt) });
  }
  if (versionName) {
    detailRows.push({ key: 'version', label: tc('release_filters_version'), value: versionName });
  }

  const popoverContent = (
    <DashboardPostPopover
      className="flex max-h-[85dvh] flex-col lg:max-h-[70vh]"
      gradient={gradient}
      headerIcon={statusIcon}
      headerLabel={statusLabel}
      headerLines={headerLines}
      postMedia={popoverMedia}
      caption={post.content || variation?.title || ''}
      thumbnail={
        mediaUrls.length === 0 ? (
          <div className="aspect-square w-full overflow-hidden rounded-xl">
            <TextPostTile text={post.content || variation?.title} emptyLabel={tc('approval_no_content')} />
          </div>
        ) : undefined
      }
      footer={
        <button
          type="button"
          onClick={openItem}
          className="w-full rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-primary-600-contrast transition-colors hover:bg-primary-700 hover:text-primary-700-contrast"
        >
          {t('release_popover_open')}
        </button>
      }
    >
      {(showBrand || pinned || detailRows.length > 0 || item.decision) && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3 text-xs">
          {showBrand && <NotificationBrandChip name={item.brand.name} logo={item.brand.logo} />}
          {pinned && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
              <Icon name={ICONS.REFRESH} size="xs" ariaHidden />
              {t('release_changes_made_marker')}
            </span>
          )}
          {detailRows.map((row) => (
            <div key={row.key} className="flex items-baseline justify-between gap-3">
              <span className="font-medium uppercase tracking-wide text-gray-500">{row.label}</span>
              <span className="text-right text-gray-800">
                {row.value}
                {row.extra && <span className={cn('ml-1', row.extraClass)}>({row.extra})</span>}
              </span>
            </div>
          ))}
          {item.decision && (
            <div className="rounded-lg bg-coral-50 p-2 text-gray-700">
              <p className="font-medium">
                {t('release_decision_by', { name: item.decision.byName })}
                <span className="ml-1 font-normal text-gray-500">{formatRelativeDate(item.decision.decidedAt)}</span>
              </p>
              {item.decision.commentExcerpt && (
                <p className="mt-1 break-words italic">&ldquo;{item.decision.commentExcerpt}&rdquo;</p>
              )}
            </div>
          )}
        </div>
      )}
    </DashboardPostPopover>
  );

  const trigger = (
    <button
      type="button"
      onClick={openItem}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          openItem();
        }
      }}
      className="group flex w-40 flex-none flex-col gap-2 rounded-xl text-left sm:w-44"
    >
      <span
        className={cn(
          'relative block aspect-square w-full overflow-hidden rounded-xl bg-gray-100',
          'transition-all duration-300 ease-out group-hover:shadow-lg',
          variation && 'ring-2 ring-secondary-300'
        )}
      >
        {mediaUrls.length > 0 ? (
          <RetryImage
            src={mediaUrls[0]}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <TextPostTile text={post.content || variation?.title} emptyLabel={tc('approval_no_content')} />
        )}

        {firstMediaIsVideo(item, mediaUrls) && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <VideoPlayBadge />
          </span>
        )}

        {extraCount > 0 && (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white backdrop-blur-sm">
            +{extraCount}
          </span>
        )}

        {needed && (
          <span
            title={needed.label}
            className={cn(
              'absolute left-1.5 top-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none shadow-sm',
              NEEDED_BY_CHIP_CLASS[needed.tone]
            )}
          >
            <span aria-hidden="true">{needed.short}</span>
            <span className="sr-only">{needed.label}</span>
          </span>
        )}

        {isNew && (
          <span
            title={t('release_new_badge')}
            className="absolute right-1.5 top-1.5 h-3 w-3 rounded-full bg-coral-500 ring-2 ring-white"
          >
            <span className="sr-only">{t('release_new_badge')}</span>
          </span>
        )}
      </span>

      <span className="block min-w-0">
        <span className="line-clamp-2 break-words text-sm font-medium leading-snug text-gray-900">
          {label}
        </span>

        {(versionName || placement || pinned) && (
          <span className="mt-1 flex flex-wrap items-center gap-1">
            {pinned && (
              <span className="inline-flex items-center gap-0.5 rounded bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                <Icon name={ICONS.REFRESH} size="xs" ariaHidden />
                {t('release_changes_made_marker')}
              </span>
            )}
            {versionName && (
              <span className="inline-flex max-w-full items-center gap-0.5 rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-800">
                <Icon name={ICONS.LANGUAGE} size="xs" ariaHidden />
                <span className="truncate">{versionName}</span>
              </span>
            )}
            {placement && (
              <span
                title={item.placement === 'live' && item.liveAt ? formatDateTime(item.liveAt) : undefined}
                className={cn(
                  'inline-flex max-w-full items-center rounded px-1.5 py-0.5 text-[10px] font-semibold',
                  placement.className
                )}
              >
                <span className="truncate">{placement.text}</span>
              </span>
            )}
          </span>
        )}

        {meta && <span className="mt-1 line-clamp-2 block break-words text-xs text-gray-500">{meta}</span>}

        {section === 'changes_requested' && item.decision?.commentExcerpt && (
          <span className="mt-0.5 line-clamp-2 block break-words text-xs italic text-gray-600">
            &ldquo;{item.decision.commentExcerpt}&rdquo;
          </span>
        )}
      </span>
    </button>
  );

  return (
    <HoverCard
      trigger={trigger}
      side="right"
      align="start"
      // Keep the trigger's tap/click action on mobile instead of wrapping it in a preview sheet.
      allowTriggerPointerEvents
      className="max-lg:!top-auto max-lg:rounded-t-2xl max-lg:!max-h-[85dvh]"
    >
      {popoverContent}
    </HoverCard>
  );
};
